import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { FileText, CheckCircle2, XCircle, ExternalLink } from "lucide-react";

export default function ContractFill() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | valid | invalid | done
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/contract-fill/${token}`);
        setData({
          guest_name: r.data.guest_name || "",
          guest_email: r.data.guest_email || "",
          reservation: r.data.reservation,
          house: r.data.house,
          contract_id: r.data.contract_id,
          profile: {
            nationality: "Brasileira",
            ...(r.data.guest_profile || {}),
          },
        });
        setState("valid");
      } catch (e) {
        setState("invalid");
      }
    })();
  }, [token]);

  const update = (k, v) => setData({ ...data, profile: { ...data.profile, [k]: v } });

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/public/contract-fill/${token}`, {
        guest_name: data.guest_name,
        guest_profile: data.profile,
      });
      setState("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar. Verifique os campos.");
    } finally { setSaving(false); }
  }

  return (
    <PublicShell>
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        {state === "loading" && <p className="text-sm text-slate-500">Carregando…</p>}

        {state === "invalid" && (
          <div className="bg-white border border-red-200 rounded p-8" data-testid="fill-invalid">
            <div className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              <h1 className="text-xl font-semibold">Link inválido ou expirado</h1>
            </div>
            <p className="text-sm text-slate-600 mt-3">
              O link para preenchimento do seu contrato não é mais válido. Entre em contato com a Azuhomes pelo WhatsApp para receber um novo.
            </p>
          </div>
        )}

        {state === "done" && (
          <div className="bg-white border border-emerald-200 rounded p-8" data-testid="fill-done">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <h1 className="text-xl font-semibold">Dados recebidos</h1>
            </div>
            <p className="text-sm text-slate-600 mt-3">
              Obrigado! Seus dados foram registrados. Em breve enviaremos o contrato final para sua leitura e aceite eletrônico.
            </p>
          </div>
        )}

        {state === "valid" && data && (
          <>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#3B9FD4]">Contrato de locação</span>
            <h1 className="font-display text-4xl text-[#0AA89C] mt-2">Complete seus dados</h1>
            <p className="text-sm text-slate-500 mt-3">
              Reserva <b>{data.reservation?.code}</b> em <b>{data.house?.name}</b> ({data.reservation?.check_in} → {data.reservation?.check_out}).
              Estes dados aparecerão no contrato conforme a Lei 8.245/91.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="fill-form">
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h2 className="text-sm font-semibold text-[#0AA89C] mb-3">Identificação</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Nome completo</Label><Input value={data.guest_name} onChange={(e) => setData({ ...data, guest_name: e.target.value })} required data-testid="fill-name" /></div>
                  <div><Label>Nacionalidade</Label><Input value={data.profile.nationality || ""} onChange={(e) => update("nationality", e.target.value)} required /></div>
                  <div><Label>Naturalidade (cidade/UF)</Label><Input value={data.profile.naturalidade || ""} onChange={(e) => update("naturalidade", e.target.value)} required /></div>
                  <div><Label>Data de nascimento</Label><Input type="date" value={data.profile.date_of_birth || ""} onChange={(e) => update("date_of_birth", e.target.value)} required /></div>
                  <div><Label>Estado civil</Label>
                    <Select value={data.profile.civil_state || ""} onValueChange={(v) => update("civil_state", v)}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="solteiro(a)">Solteiro(a)</SelectItem>
                        <SelectItem value="casado(a)">Casado(a)</SelectItem>
                        <SelectItem value="divorciado(a)">Divorciado(a)</SelectItem>
                        <SelectItem value="viúvo(a)">Viúvo(a)</SelectItem>
                        <SelectItem value="união estável">União estável</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Profissão</Label><Input value={data.profile.profession || ""} onChange={(e) => update("profession", e.target.value)} required /></div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h2 className="text-sm font-semibold text-[#0AA89C] mb-3">Documento de identificação</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tipo</Label>
                    <Select value={data.profile.doc_type || ""} onValueChange={(v) => update("doc_type", v)}>
                      <SelectTrigger data-testid="fill-doc-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="rg">RG</SelectItem>
                        <SelectItem value="passport">Passaporte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Número</Label><Input value={data.profile.doc_number || ""} onChange={(e) => update("doc_number", e.target.value)} required /></div>
                  <div><Label>Órgão emissor</Label><Input value={data.profile.doc_emissor || ""} onChange={(e) => update("doc_emissor", e.target.value)} /></div>
                  <div><Label>Data de emissão</Label><Input type="date" value={data.profile.doc_emit_date || ""} onChange={(e) => update("doc_emit_date", e.target.value)} /></div>
                  <div><Label>Telefone alternativo</Label><Input value={data.profile.phone_alt || ""} onChange={(e) => update("phone_alt", e.target.value)} /></div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h2 className="text-sm font-semibold text-[#0AA89C] mb-3">Endereço de cobrança</h2>
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-4"><Label>Logradouro</Label><Input value={data.profile.address_street || ""} onChange={(e) => update("address_street", e.target.value)} required /></div>
                  <div className="col-span-2"><Label>Número</Label><Input value={data.profile.address_number || ""} onChange={(e) => update("address_number", e.target.value)} required /></div>
                  <div className="col-span-3"><Label>Complemento</Label><Input value={data.profile.address_complement || ""} onChange={(e) => update("address_complement", e.target.value)} /></div>
                  <div className="col-span-3"><Label>Bairro</Label><Input value={data.profile.address_neighborhood || ""} onChange={(e) => update("address_neighborhood", e.target.value)} required /></div>
                  <div className="col-span-3"><Label>Cidade</Label><Input value={data.profile.address_city || ""} onChange={(e) => update("address_city", e.target.value)} required /></div>
                  <div className="col-span-1"><Label>UF</Label><Input maxLength={2} value={data.profile.address_state || ""} onChange={(e) => update("address_state", e.target.value.toUpperCase())} required /></div>
                  <div className="col-span-2"><Label>CEP</Label><Input value={data.profile.address_postal_code || ""} onChange={(e) => update("address_postal_code", e.target.value)} required /></div>
                </div>
              </div>

              <div className="text-xs text-slate-500">
                Ao enviar, você declara que as informações são verdadeiras. Após o preenchimento, a Azuhomes enviará o contrato final para sua leitura e aceite eletrônico (nos termos da Lei 14.063/2020).
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="fill-submit">
                  {saving ? "Enviando…" : "Enviar dados"}
                </Button>
              </div>
            </form>
          </>
        )}
      </section>
    </PublicShell>
  );
}
